import { ref, computed, watch, onBeforeUnmount } from '@vue/composition-api'

export default function useHybridPagination({ isClientPaginatedMode }) {
  const fullList = ref([])
  const serverItems = ref([])
  const serverTotal = ref(0)
  const perPage = ref(10)
  const perPageOptions = [10, 25, 50, 100]
  const currentPage = ref(1)
  const searchQuery = ref('')
  const debouncedSearchQuery = ref('')

  let searchDebounceTimer = null

  const filteredFullList = computed(() => {
    const q = (debouncedSearchQuery.value || '').toLowerCase()
    if (!q) return fullList.value
    return fullList.value.filter(m => {
      const fullname = (m.fullname || '').toLowerCase()
      const username = (m.username || '').toLowerCase()
      const email = (m.email || '').toLowerCase()
      return fullname.includes(q) || username.includes(q) || email.includes(q)
    })
  })

  const tableItems = computed(() => {
    if (isClientPaginatedMode.value) {
      const start = (currentPage.value - 1) * perPage.value
      return filteredFullList.value.slice(start, start + perPage.value)
    }
    return serverItems.value
  })

  const totalUsers = computed(() => (
    isClientPaginatedMode.value ? filteredFullList.value.length : serverTotal.value
  ))

  const dataMeta = computed(() => {
    const localItemsCount = tableItems.value.length
    const from = totalUsers.value === 0 ? 0 : perPage.value * (currentPage.value - 1) + 1
    return {
      from,
      to: totalUsers.value === 0 ? 0 : Math.min(from + localItemsCount - 1, totalUsers.value),
      of: totalUsers.value,
    }
  })

  function resetLists() {
    serverItems.value = []
    serverTotal.value = 0
    fullList.value = []
  }

  function resetSearch() {
    searchQuery.value = ''
    debouncedSearchQuery.value = ''
  }

  function setupSearchDebounce(onDebounced) {
    watch(searchQuery, newValue => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
      searchDebounceTimer = setTimeout(() => {
        debouncedSearchQuery.value = newValue
        currentPage.value = 1
        onDebounced()
      }, 300)
    })

    onBeforeUnmount(() => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    })
  }

  return {
    fullList,
    serverItems,
    serverTotal,
    perPage,
    perPageOptions,
    currentPage,
    searchQuery,
    debouncedSearchQuery,
    filteredFullList,
    tableItems,
    totalUsers,
    dataMeta,
    resetLists,
    resetSearch,
    setupSearchDebounce,
  }
}
